import { iconByProduct } from 'features/subscriptions/utils';
import { forIn, get, pick } from 'lodash';
import { ResourceByProductType } from './types/types';
import { CONTEXT_IDS_TEXT, TenantProductCode } from './constants';
import {
  isEmbeddedResourceProductCode,
  USER_SEARCH_RESOURCE_ID,
} from './hooks/embeddedResources';

export const RESOURCES_CONFIG = {
  cdp: { label: 'CDP', icon: iconByProduct.cdp },
  discover: { label: 'Search', icon: iconByProduct.search },
  edge: { label: 'Edge', icon: iconByProduct.xmcloud },
  personalize: { label: 'Personalize', icon: iconByProduct.personalize },
  search: { label: 'Search', icon: iconByProduct.search },
  xmcloud: { label: 'SitecoreAI', icon: iconByProduct.xmcloud },
} as const;

export const validResources = Object.fromEntries(
  Object.entries(RESOURCES_CONFIG).map(([key, config]) => [key, config.icon]),
);

export const isSearchProductCode = (productCode?: string): boolean => {
  const normalizedCode = (productCode ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  return normalizedCode === 'discover' || normalizedCode.includes('search');
};

export type AvailableProductLike = {
  productCode?: string;
  productCodes?: string[];
  tenantId?: string;
  tenantName?: string;
};

export type TenantLabelLike = {
  key?: string;
  value?: string;
};

export type TenantNodeLike = {
  id?: string;
  systemId?: string;
  name?: string;
  displayName?: string;
  productCode?: string;
  labels?: TenantLabelLike[] | null;
  annotations?: TenantLabelLike[] | null;
};

export type SearchTenantCandidate = {
  value: string;
  label: string;
  productCode?: string;
};

export const getAvailableProductsArray = (
  payload: unknown,
): AvailableProductLike[] => {
  if (Array.isArray(payload)) return payload as AvailableProductLike[];

  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return (payload as { data: AvailableProductLike[] }).data;
  }

  return [];
};

export const isSearchAvailableProduct = (
  product: AvailableProductLike,
): boolean => {
  if (isSearchProductCode(product.productCode)) return true;
  return (product.productCodes ?? []).some(isSearchProductCode);
};

export const isSearchTenantNode = (node: TenantNodeLike): boolean => {
  if (isSearchProductCode(node.productCode)) return true;
  return (node.labels ?? []).some(
    (label) =>
      (label.key ?? '').toLowerCase() === 'product' &&
      isSearchProductCode(label.value),
  );
};

export const getSearchTenantOptionsFromTenantNodes = (
  nodes: TenantNodeLike[] | null | undefined,
): { value: string; label: string }[] =>
  (nodes ?? [])
    .filter(isSearchTenantNode)
    .map((node) => ({
      value: getTenantIdFromTenantNode(node),
      label: node.displayName ?? node.name ?? node.id ?? 'Search environment',
    }))
    .filter((option) => !!option.value);

export const mergeTenantOptions = <T extends { value: string; label: string }>(
  ...groups: Array<T[]>
): T[] => {
  const merged = groups.flat();
  const seen = new Set<string>();

  return merged.filter((item) => {
    if (seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
};

const TENANT_ID_HINT_KEYS = new Set([
  'tenantid',
  'tenant-id',
  'tenant_id',
  'platformtenantid',
  'platform-tenant-id',
  'platform_tenant_id',
]);

export const getTenantIdFromTenantNode = (node: TenantNodeLike): string => {
  const annotationTenantId = (node.annotations ?? []).find((annotation) =>
    TENANT_ID_HINT_KEYS.has((annotation.key ?? '').toLowerCase()),
  )?.value;

  return annotationTenantId ?? node.id ?? node.systemId ?? '';
};

export const buildSearchTenantCandidates = ({
  availableProducts,
  tenantNodes,
}: {
  availableProducts: AvailableProductLike[];
  tenantNodes: TenantNodeLike[] | null | undefined;
}): SearchTenantCandidate[] => {
  const candidatesFromAvailableProducts = availableProducts
    .filter(isSearchAvailableProduct)
    .map((availableProduct) => ({
      value: availableProduct.tenantId ?? '',
      label:
        availableProduct.tenantName ??
        availableProduct.tenantId ??
        'Search environment',
      productCode:
        availableProduct.productCode ??
        (availableProduct.productCodes ?? []).find(isSearchProductCode),
    }))
    .filter((tenantCandidate) => !!tenantCandidate.value);

  const candidatesFromTenantNodes = getSearchTenantOptionsFromTenantNodes(
    tenantNodes,
  ).map((searchTenantOption) => {
    const matchingTenantNode = (tenantNodes ?? []).find(
      (tenantNode) =>
        getTenantIdFromTenantNode(tenantNode ?? {}) ===
        searchTenantOption.value,
    );
    const productLabel = (matchingTenantNode?.labels ?? []).find(
      (label) => (label?.key ?? '').toLowerCase() === 'product',
    )?.value;

    return {
      ...searchTenantOption,
      productCode: matchingTenantNode?.productCode ?? productLabel,
    };
  });

  return mergeTenantOptions(
    candidatesFromAvailableProducts,
    candidatesFromTenantNodes,
  );
};

/**
 * Resolves the icon+name object for a context resource.  Returns the same
 * shape as `validResources[key]` so callers can destructure `{ iconUrl, name }`.
 *
 * The EP API stamps tenantProductCode "SearchEmbedded" onto user-created
 * Search resources (same tenant as the embedded one), so we cannot rely on
 * tenantProductCode alone.  For the well-known user-managed Search resource
 * we fall back to the 'search' config entry.
 */
export const getResourceDisplayConfig = ({
  tenantProductCode,
  id,
  type,
}: {
  tenantProductCode?: string;
  id?: string;
  type?: string;
}) => {
  const byCode =
    validResources[
      tenantProductCode?.toLowerCase() as keyof typeof validResources
    ];
  if (byCode) return byCode;

  if (id === USER_SEARCH_RESOURCE_ID) return validResources.search;

  // Embedded product codes are not user-displayable by default; only the
  // known user-managed Search resource id is whitelisted above.
  if (isEmbeddedResourceProductCode(tenantProductCode)) return undefined;

  // Fall back to resource.type when tenantProductCode is absent or unrecognised
  // (e.g. resources that omit tenantProductCode but have a known type like 'search', 'cdp', 'edge')
  const byType =
    validResources[type?.toLowerCase() as keyof typeof validResources];
  if (byType) return byType;

  return undefined;
};

/**
 * Returns true if the resource should be displayed in the listing/detail tables.
 * Mirrors the validResources lookup but also handles the user-created Search
 * resource whose tenantProductCode is "SearchEmbedded" (an EP API quirk).
 */
export const isDisplayableResource = ({
  tenantProductCode,
  id,
  type,
}: {
  tenantProductCode?: string;
  id?: string;
  type?: string;
}): boolean => !!getResourceDisplayConfig({ tenantProductCode, id, type });

export function pickObjectsDifferences<T>(source: T, comparator: T): any {
  const keysToPick: string[] = [];
  forIn(source, (value, key) => {
    if (value !== get(comparator, key)) {
      keysToPick.push(key);
    }
  });

  return keysToPick.length ? pick(source, keysToPick) : null;
}

const CHARSET =
  '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function encode(int: number) {
  if (int === 0) {
    return CHARSET[0];
  }

  let res = '';
  while (int > 0) {
    res = CHARSET[int % 62] + res;
    int = Math.floor(int / 62);
  }
  return res;
}

export function guidToBase62(guid: string): string {
  const hex = guid.replace(/-/g, '');
  const decimal = BigInt('0x' + hex);
  return encode(Number(decimal));
}

export const generateContextId = (guid: string) => guidToBase62(guid);

export const groupResourcesByType = (resources: ResourceByProductType[]) =>
  resources.reduce((resourcesByType, resource) => {
    const productCodeKey = resource.productCode as keyof typeof resourcesByType;
    if (!resourcesByType[productCodeKey]) {
      //@ts-ignore
      resourcesByType[productCodeKey] = [];
    }
    //@ts-ignore
    resourcesByType[productCodeKey].push(resource);
    return resourcesByType;
  }, {});

export const addShowHeaderFlag = (resources?: ResourceByProductType[]) => {
  const projectNames = new Set();
  return (
    resources?.map((resource) => {
      // for non live-preview tenants
      if (!resource.environment) return resource;

      const projectName = resource.tenantName;
      if (!projectNames.has(projectName)) {
        projectNames.add(projectName);
        return { ...resource, showHeader: true };
      } else {
        return { ...resource, showHeader: false };
      }
    }) ?? []
  );
};

export const transformResources = (resources: ResourceByProductType[]) => {
  const groupedResourcesByType = groupResourcesByType(resources);
  const transformedXMCTenants = addShowHeaderFlag(
    groupedResourcesByType['XMCloud' as keyof typeof groupedResourcesByType],
  );

  if (transformedXMCTenants.length)
    //@ts-ignore
    groupedResourcesByType['XMCloud' as keyof typeof groupedResourcesByType] =
      transformedXMCTenants;

  return groupedResourcesByType;
};

export const getResourcePayload = ({
  tenantId,
  productCode,
  environment,
}: ResourceByProductType) => ({
  tenantId,
  productCode,
  environment,
});

/**
 * Checks if a resource is sensitive (Edge Preview or Edge Live)
 * These resources should only be used in server-side context IDs
 *
 * @param resourceName - User-defined resource name (can be any casing)
 * @param tenantProductCode - Backend value: 'Edge' or 'XMCloud' (capitalized)
 * @param type - Backend value: 'edge', 'site', 'files', etc. (lowercase)
 */
export const isSensitiveResource = (
  resourceName?: string,
  tenantProductCode?: string,
  type?: string,
): boolean => {
  const resourceNameLower = resourceName?.toLowerCase() || '';
  const hasEdgeKeywords =
    (resourceNameLower.includes('edge') &&
      (resourceNameLower.includes(CONTEXT_IDS_TEXT.PREVIEW_STATUS) ||
        resourceNameLower.includes(CONTEXT_IDS_TEXT.LIVE_STATUS))) ||
    tenantProductCode === TenantProductCode.EDGE;

  const isEdgeResource =
    type === 'edge' &&
    (tenantProductCode === TenantProductCode.EDGE ||
      tenantProductCode === TenantProductCode.XMCLOUD);

  return hasEdgeKeywords || isEdgeResource;
};

export const validateProductSelection = (
  productCode: string,
  selectedProducts: { productCode: string; tenants: ResourceByProductType[] }[],
): boolean => {
  const isCDPSelected = selectedProducts.some(
    (product) => product.productCode === validResources.cdp.name,
  );
  const isPersonalizeSelected = selectedProducts.some(
    (product) => product.productCode === validResources.personalize.name,
  );

  return (
    (productCode === validResources.cdp.name && isPersonalizeSelected) ||
    (productCode === validResources.personalize.name && isCDPSelected)
  );
};
